/* Hero holographic glitch overlay (v2, perf pass 2026-11).
   Small patches pop in on a random cadence; each is filled with a sweeping
   pink/purple/blue/green holographic gradient, sliced + scanlined for a glitch
   look. The canvas uses mix-blend-mode (set on the element) so the patches read
   as a hue-shifted distortion of the video underneath.

   Performance notes (look is unchanged):
   - The old loop ran requestAnimationFrame every frame forever, even when no
     patch was on screen. Any running rAF loop forces the browser to build a new
     frame 60 times a second, while a plain video needs none. Now the loop only
     runs while patches are alive; between bursts it sleeps on a timer.
   - While idle the canvas is hidden (visibility), so there is no blended layer
     over the video at all for most of the time.
   - On touch devices only: frames are capped at ~30 fps (the effect is a jittery
     glitch, so this reads the same), the canvas renders at 1x pixel density
     (the patches are blocky bars), and each patch's gradient is built once at
     spawn instead of every frame. Touch is detected in JS, not with a media
     query: stylus-capable phones and desktop-site mode defeat
     (hover:none)/(pointer:coarse). */
(function () {
  "use strict";

  function isTouch() {
    try {
      if (window.matchMedia && matchMedia("(any-pointer: coarse)").matches) return true;
    } catch (e) {}
    return ("ontouchstart" in window) || (navigator.maxTouchPoints > 0);
  }

  function init(canvas) {
    if (canvas.__hvsGlitch) return;
    canvas.__hvsGlitch = true;
    var reduce = false;
    try { reduce = matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
    if (reduce) return;                       // respect reduced-motion: no glitch

    var touch = isTouch();
    var FRAME_MS = touch ? 33 : 0;            // ~30 fps cap on touch only

    var d = canvas.dataset;
    var cfg = {
      minMs:   Math.max(100, (parseFloat(d.glitchMin)  || 0.5) * 1000),
      maxMs:   Math.max(150, (parseFloat(d.glitchMax)  || 2.0) * 1000),
      count:   Math.max(1,   parseInt(d.glitchCount, 10) || 2),
      maxSize: (parseFloat(d.glitchSize)    || 18) / 100,   // fraction of min(W,H)
      opacity: (parseFloat(d.glitchOpacity) || 70) / 100,
      sweepMs: Math.max(400, (parseFloat(d.glitchSweep) || 4) * 1000),
      colors:  (d.glitchColors || "#ff6ec7,#b06ef5,#6ec7ff,#6effa8")
                 .split(",").map(function (c) { return c.trim(); }).filter(Boolean)
    };
    if (cfg.maxMs < cfg.minMs) { var t = cfg.maxMs; cfg.maxMs = cfg.minMs; cfg.minMs = t; }

    var ctx = canvas.getContext("2d");
    var W = 0, H = 0, dpr = 1, lastCssW = -1, lastCssH = -1;
    function size() {
      var r = canvas.getBoundingClientRect();
      // Mobile URL-bar show/hide fires resize constantly; only reallocate the
      // backing store when the box actually changed size.
      if (Math.abs(r.width - lastCssW) < 1 && Math.abs(r.height - lastCssH) < 1) return;
      lastCssW = r.width; lastCssH = r.height;
      dpr = touch ? 1 : Math.min(2, window.devicePixelRatio || 1);
      W = canvas.width  = Math.max(2, Math.round(r.width  * dpr));
      H = canvas.height = Math.max(2, Math.round(r.height * dpr));
    }
    size();
    if ("ResizeObserver" in window) new ResizeObserver(size).observe(canvas);
    else window.addEventListener("resize", size);

    function rand(a, b) { return a + Math.random() * (b - a); }

    // Holographic gradient across a patch, hue order rotated by the global sweep
    // and the patch position so the colours appear to travel.
    function holoGrad(p, sweep) {
      var g = ctx.createLinearGradient(p.x, p.y, p.x + p.w, p.y + p.h * 0.4);
      var cols = cfg.colors, n = cols.length;
      var rot = Math.floor((sweep + p.x / W) * n);
      var steps = n * 2;
      for (var s = 0; s <= steps; s++) {
        var idx = ((s + rot) % n + n) % n;
        g.addColorStop(Math.min(1, s / steps), cols[idx]);
      }
      return g;
    }

    var patches = [];
    function spawn(now) {
      var sweep = (now % cfg.sweepMs) / cfg.sweepMs;
      for (var i = 0; i < cfg.count; i++) {
        var base = Math.min(W, H) * rand(0.04, cfg.maxSize);
        var pw = base * rand(1.0, 2.6);                 // wide glitch bars
        var ph = base * rand(0.25, 0.9);
        var p = {
          x: rand(0, Math.max(1, W - pw)), y: rand(0, Math.max(1, H - ph)),
          w: pw, h: ph, t0: now, life: rand(90, 340),
          slices: Math.floor(rand(2, 6)), flip: Math.random() < 0.5, grad: null
        };
        if (touch) p.grad = holoGrad(p, sweep);       // built once, reused
        patches.push(p);
      }
    }

    var raf = null, timer = null, running = false, shown = false;
    var nextSpawn = 0, lastDraw = 0;

    function show(on) {
      if (on === shown) return;
      shown = on;
      canvas.style.visibility = on ? "visible" : "hidden";
    }

    function scheduleSpawn(now) {
      // Sleep until the next burst instead of spinning rAF on an empty canvas.
      var wait = Math.max(0, nextSpawn - now);
      timer = setTimeout(function () {
        timer = null;
        if (running) raf = requestAnimationFrame(frame);
      }, wait);
    }

    function frame(now) {
      raf = null;
      if (!running) return;
      if (now >= nextSpawn) { spawn(now); nextSpawn = now + rand(cfg.minMs, cfg.maxMs); }

      if (FRAME_MS && now - lastDraw < FRAME_MS - 1 && patches.length) {
        raf = requestAnimationFrame(frame);           // skip this vsync, keep timing
        return;
      }
      lastDraw = now;

      ctx.clearRect(0, 0, W, H);
      var sweep = (now % cfg.sweepMs) / cfg.sweepMs;

      for (var i = patches.length - 1; i >= 0; i--) {
        var p = patches[i];
        var age = (now - p.t0) / p.life;
        if (age >= 1) { patches.splice(i, 1); continue; }
        var fl = Math.sin(Math.min(1, age) * Math.PI);          // 0..1..0 flicker
        ctx.globalAlpha = cfg.opacity * fl * rand(0.65, 1.0);
        ctx.fillStyle = p.grad || holoGrad(p, sweep);

        var sliceH = p.h / p.slices;
        for (var sI = 0; sI < p.slices; sI++) {
          var shift = (Math.random() < 0.55) ? rand(-p.w * 0.18, p.w * 0.18) : 0;
          ctx.fillRect(p.x + shift, p.y + sI * sliceH, p.w, sliceH * rand(0.55, 1));
        }

        // scanline gaps (punch thin transparent lines through the patch)
        ctx.globalCompositeOperation = "destination-out";
        ctx.globalAlpha = 0.3;
        var step = Math.max(2, Math.round(2 * dpr));
        for (var yy = p.y; yy < p.y + p.h; yy += step) ctx.fillRect(p.x, yy, p.w, Math.max(1, dpr));
        ctx.globalCompositeOperation = "source-over";
      }
      ctx.globalAlpha = 1;

      if (patches.length) {
        show(true);
        raf = requestAnimationFrame(frame);
      } else {
        // Burst finished: canvas is already clear. Hide it and sleep.
        show(false);
        scheduleSpawn(now);
      }
    }

    function start() {
      if (running) return;
      running = true; nextSpawn = 0;
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      if (timer) { clearTimeout(timer); timer = null; }
      ctx.clearRect(0, 0, W, H); patches.length = 0; show(false);
    }

    show(false);
    // Pause when offscreen or tab hidden (save CPU on an idle effect).
    var inView = true;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { inView = e.isIntersecting; inView && !document.hidden ? start() : stop(); });
      }, { threshold: 0.01 }).observe(canvas);
    } else { start(); }
    document.addEventListener("visibilitychange", function () {
      document.hidden || !inView ? stop() : start();
    });
  }

  function boot() {
    var nodes = document.querySelectorAll("[data-hvs-glitch]");
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener("shopify:section:load", boot);
})();
